import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { User } from '@/reducers/auth/userReducer'
import { TaxFilingUser } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

import {
  getUserDocumentCategoriesById,
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.selectors'
import { documentCategoryOptions } from '@/featuresV1/Admin/Documents/helpers'
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { selectOnboardingStepCompletedOrSkipped } from '@/featuresV1/Onboarding/UserOnboardingSteps/onboarding.selectors'
import { fetchUserOnboardingStepsIfNeeded } from '@/featuresV1/Onboarding/UserOnboardingSteps/onboarding.actions'
import { GettingStartedOnboardingStep } from '@/featuresV1/Onboarding/UserOnboardingSteps/onboarding.reducer'
import { ViewMissingStatementsAccordionForCatchupBookkeeping } from '@/featuresV1/Dashboard/OnboardingDashboard/GettingStartedItems/OnboardingItemCTAContent'
import MissingStatements from '@/featuresV1/UserDocuments/sharedComponents/MissingStatements'
import { DescriptionType } from '@/featuresV1/UserDocuments/constants'

import {
  getBookkeepingTitle,
  formatMonthYear,
  transformMonthYear,
} from '../lib/helpers'
import { IDocumentTypes } from '../lib/types'
import {
  StatementUploadSection,
  StatementData,
} from '../StatementUploadSection'
import { Description } from './Description'

// TODO: Migrate DatePicker to V2 skipcq: JS-0099
import { DatePicker } from '@/base'
import { TypographyStyles } from '@/styles'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Dropdown,
  Icon,
  Link,
  List,
  Popup,
  Separator,
  Text,
} from '@/ui'

interface IContentCard {
  descriptionType?: DescriptionType | null
  documentCategoryMap?: Map<UploadDocumentType, string[]>
  documentType?: UploadDocumentType
  error: string
  hideDocumentCategoryMixed?: boolean
  internalCategoryNameFlag: string
  internalCategoryNames?: string[]
  isMixedType?: boolean
  isUploadOnly?: boolean
  requiredDocumentCount?: number
  sentCategoryId?: number | null
  sentCategoryInternalName?: string
  sentDescription?: React.ReactNode
  sentDocumentType?: UploadDocumentType
  sentFinancialAccountId?: number[]
  sentMonthYear?: string
  sentSubHeaderText?: string
  sentTitle?: string
  sentYear?: string
  setDocumentType: (documentType: UploadDocumentType) => void
  setError: (error: string) => void
  setHideDocumentCategoryMixed?: (hide: boolean) => void
  setInternalCategoryNames?: (names: string[]) => void
  setMonth: (month: number) => void
  setStatementData?: (data: StatementData[]) => void
  setTaxDocumentType?: (type: 'federal' | 'state') => void
  setUploadOverride: (override: boolean) => void
  setYear?: (year: string) => void
  statementData?: StatementData[]
  taxDocumentType?: 'federal' | 'state'
  taxFilingFormId?: number
  taxYearDisabled?: boolean
  uploadedDocumentCount?: number
  useFilteredAccountOptions?: boolean
  user?: User | TaxFilingUser
  userFacing?: boolean
}

export const ContentCard = ({
  descriptionType,
  documentCategoryMap,
  documentType = UploadDocumentType.STATEMENT,
  error,
  hideDocumentCategoryMixed = false,
  internalCategoryNameFlag,
  internalCategoryNames = [],
  isMixedType = false,
  isUploadOnly = false,
  requiredDocumentCount = 0,
  sentCategoryId,
  sentCategoryInternalName,
  sentDescription,
  sentDocumentType = UploadDocumentType.STATEMENT,
  sentFinancialAccountId,
  sentMonthYear,
  sentSubHeaderText,
  sentTitle,
  sentYear,
  setDocumentType = (_documentType: UploadDocumentType) => {},
  setError,
  setHideDocumentCategoryMixed = (_hide: boolean) => {},
  setInternalCategoryNames = (_names: string[]) => {},
  setMonth = (_month: number) => {},
  setStatementData = (_data: StatementData[]) => {},
  setTaxDocumentType = (_type: 'federal' | 'state') => {},
  setUploadOverride,
  statementData = [],
  taxDocumentType,
  taxFilingFormId,
  taxYearDisabled = false,
  uploadedDocumentCount = 0,
  useFilteredAccountOptions = true,
  user,
  userFacing = false,
}: IContentCard) => {
  const dispatch = useAppDispatch()
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const documentCategories = useReselector(getUserDocumentCategoriesById)
  const nextTaxYear = (Number(currentTaxYear) + 1).toString()

  const [documentTypes] = useState<IDocumentTypes[]>([
    { value: UploadDocumentType.BOOKKEEPING, text: 'Bookkeeping Documents' },
    { value: UploadDocumentType.TAX, text: 'Tax Documents' },
  ])

  const [year, setYear] = useState(
    sentYear ?? transformMonthYear(sentMonthYear)?.year()?.toString()
  )

  const [userDocumentCategoryId, setUserDocumentCategoryId] = useState(
    sentCategoryId ?? undefined
  )

  const filteredDocumentCategories = useMemo(
    () =>
      documentCategoryOptions(
        documentCategories,
        documentType,
        false,
        internalCategoryNames
      ),
    [documentCategories, documentType, internalCategoryNames]
  )

  const sentCategoryById = useReselector(
    selectUserDocumentCategory,
    sentCategoryId
  )

  const sentCategoryByName = useReselector(
    selectUserDocumentCategoryByInternalName,
    sentCategoryInternalName
  )

  const sentCategoryByInternalName = useReselector(
    selectUserDocumentCategoryByInternalName,
    internalCategoryNameFlag
  )

  useEffect(() => {
    if (isMixedType) {
      if (documentCategoryMap) {
        const filteredInternalNames = documentCategoryMap.get(documentType)

        if ((filteredInternalNames?.length ?? 0) > 0) {
          setInternalCategoryNames(filteredInternalNames ?? [])
          setUserDocumentCategoryId(undefined)
          setHideDocumentCategoryMixed(false)
        } else {
          setUserDocumentCategoryId(sentCategoryByInternalName?.id ?? undefined)
          setHideDocumentCategoryMixed(true)
        }
      }
    }
  }, [
    documentCategoryMap,
    documentType,
    isMixedType,
    sentCategoryByInternalName?.id,
    setHideDocumentCategoryMixed,
    setInternalCategoryNames,
  ])

  const sentCategory = sentCategoryById || sentCategoryByName
  const inputRef = useRef<HTMLInputElement>(null)

  const isAnnualCategory = useMemo(() => {
    return Object.values(documentCategories).find(
      (dc) => dc.id === userDocumentCategoryId
    )?.isAnnual
  }, [userDocumentCategoryId, documentCategories])

  const uploadBankStatementOnboardingStepIsDone = useReselector(
    selectOnboardingStepCompletedOrSkipped,
    GettingStartedOnboardingStep.uploadBankStatements
  )

  const shouldRenderCatchupBKDescription =
    !uploadBankStatementOnboardingStepIsDone &&
    documentType === UploadDocumentType.STATEMENT

  const shouldRenderMissingStatements =
    documentType === UploadDocumentType.STATEMENT

  useEffect(() => {
    setDocumentType(sentDocumentType)
  }, [sentDocumentType, setDocumentType])

  // If the category id prop changes update the local state
  useEffect(() => {
    setUserDocumentCategoryId(sentCategory?.id ?? undefined)
  }, [sentCategory?.id])

  // If a sent month is sent update year and month local state
  useEffect(() => {
    if (!sentYear) {
      setYear(transformMonthYear(sentMonthYear)?.year()?.toString())
      setMonth(transformMonthYear(sentMonthYear)?.month() ?? 0)
    }
  }, [sentMonthYear, setMonth, sentYear])

  // if Bank Statement modal fetch financial account information when needed
  useEffect(() => {
    if (documentType === UploadDocumentType.STATEMENT) {
      if (userFacing) {
        dispatch(fetchFinancialAccountsIfNeeded())
      } else if (user) {
        dispatch(fetchUserFinancialAccounts(user.id, true))
      }
    }
  }, [documentType, userFacing, user, dispatch])

  useEffect(() => {
    dispatch(fetchUserDocumentCategoriesIfNeeded())

    dispatch(
      fetchUserOnboardingStepsIfNeeded({
        expanded: true,
      })
    )
  }, [dispatch])

  const handleRemove = useCallback(
    (index: number) =>
      setFilesToUpload([
        ...filesToUpload.slice(0, index),
        ...filesToUpload.slice(index + 1),
      ]),
    [filesToUpload]
  )

  const title = useMemo(() => {
    if (sentTitle) {
      return sentTitle
    } else if (
      documentType === UploadDocumentType.BOOKKEEPING &&
      sentCategory
    ) {
      return getBookkeepingTitle(sentCategory, descriptionType)
    } else if (sentCategory?.title) {
      return sentCategory.title
    } else if (documentType === UploadDocumentType.TAX) {
      return 'Tax Documents'
    } else if (documentType === UploadDocumentType.INCORPORATION) {
      return 'Incorporation Documents'
    } else if (documentType === UploadDocumentType.OTHER) {
      return 'Other Documents'
    } else if (documentType === UploadDocumentType.RECEIPT) {
      return 'Receipts'
    } else if (documentType === UploadDocumentType.BOOKKEEPING) {
      return 'Bookkeeping Documents'
    }
    return 'Bank Statements'
  }, [sentCategory, documentType, sentTitle, descriptionType])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files)

      const remainingRequired = requiredDocumentCount
        ? requiredDocumentCount - uploadedDocumentCount - filesToUpload.length
        : undefined

      const filesToAdd =
        remainingRequired !== undefined
          ? filesArray.slice(0, remainingRequired)
          : filesArray

      if (
        remainingRequired !== undefined &&
        filesArray.length > remainingRequired
      ) {
        setError(
          `Only ${remainingRequired} more file${remainingRequired === 1 ? '' : 's'} needed. Additional files were not added.`
        )
      }

      setFilesToUpload((prev) => [...prev, ...filesToAdd])

      if (documentType === UploadDocumentType.STATEMENT) {
        const defaultStatementData = filesToAdd.map(() => ({
          statementMonth: formatMonthYear(sentMonthYear) || '',
          financialAccountIds: sentFinancialAccountId || [],
        }))

        setStatementData([...statementData, ...defaultStatementData])
      }

      setUploadOverride(false)
      e.target.value = ''
    }
  }

  const uploadCountText = `Uploaded ${uploadedDocumentCount} of ${requiredDocumentCount || ' '} documents`

  const isDocumentSelectionDisabled = Boolean(
    requiredDocumentCount && uploadedDocumentCount >= requiredDocumentCount
  )

  const showStatementUploadSelection =
    Boolean(filesToUpload.length) &&
    documentType === UploadDocumentType.STATEMENT &&
    user

  const showDocCategoryDropdown =
    [
      UploadDocumentType.TAX,
      UploadDocumentType.INCORPORATION,
      UploadDocumentType.BOOKKEEPING,
    ].includes(documentType) &&
    !sentCategory?.id &&
    !isUploadOnly &&
    !hideDocumentCategoryMixed

  const showDatePicker =
    isAnnualCategory ||
    (documentType === UploadDocumentType.TAX &&
      !userDocumentCategoryId &&
      !isUploadOnly)

  return (
    <Card color={CardColors.LEVEL_1} className="p-6">
      <div className="flex flex-col space-y-4">
        {error && <Alert variant="error" title={error} />}

        <div>
          <Text variant={TypographyStyles.H2}>{title}</Text>

          <Text>{sentSubHeaderText || uploadCountText}</Text>
        </div>

        <Description
          descriptionType={descriptionType}
          sentDescription={sentDescription}
          documentType={documentType}
          sentCategoryId={sentCategory?.id}
          sentMonthYear={sentMonthYear}
          sentYear={sentYear?.toString()}
          forCatchUpBK={shouldRenderCatchupBKDescription}
          missingStatements={shouldRenderMissingStatements}
        />

        {shouldRenderCatchupBKDescription ? (
          <ViewMissingStatementsAccordionForCatchupBookkeeping />
        ) : shouldRenderMissingStatements ? (
          <MissingStatements includeUploadButton={false} accordionOnly />
        ) : null}

        <div className="w-full lg:w-7/12">
          <Button
            iconLeft={regular('upload')}
            onClick={() => inputRef.current?.click()}
            disabled={isDocumentSelectionDisabled}
          >
            Select Documents
          </Button>

          <input
            type="file"
            multiple
            hidden
            onChange={handleFileInput}
            ref={inputRef}
          />
        </div>

        {documentType !== UploadDocumentType.STATEMENT &&
          Boolean(filesToUpload.length) && (
            <>
              <Separator />

              <Text variant={TypographyStyles.EYEBROW}>Files to Upload</Text>

              <List>
                {filesToUpload.map((file, index) => (
                  <List.Item key={file.name}>
                    <Text> {file.name}</Text>

                    <Icon
                      icon={regular('trash-can')}
                      className="ml-2 cursor-pointer"
                      onClick={() => handleRemove(index)}
                    />
                  </List.Item>
                ))}
              </List>
            </>
          )}

        {showStatementUploadSelection && (
          <StatementUploadSection
            filesToUpload={filesToUpload}
            setFilesToUpload={setFilesToUpload}
            statementData={statementData}
            setStatementData={setStatementData}
            user={user}
            useFilteredAccountOptions={useFilteredAccountOptions}
          />
        )}

        {isMixedType && (
          <>
            <Separator />

            <Dropdown>
              <Dropdown.Trigger>
                {documentType || 'Select Document Type'}
              </Dropdown.Trigger>

              <Dropdown.Content>
                <Dropdown.Label>Select Document Type</Dropdown.Label>
                <Dropdown.Separator />

                {documentTypes.map((option) => (
                  <Dropdown.Item
                    key={option.value}
                    onClick={() =>
                      setDocumentType(option.value as UploadDocumentType)
                    }
                  >
                    {option.text}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown>
          </>
        )}

        {showDocCategoryDropdown && (
          <>
            <Separator />

            <Dropdown>
              <Dropdown.Trigger>
                {userDocumentCategoryId
                  ? filteredDocumentCategories.find(
                      (cat) => cat.value === userDocumentCategoryId
                    )?.text
                  : 'Select Document Category'}
              </Dropdown.Trigger>

              <Dropdown.Content>
                <Dropdown.Label>Select Document Category</Dropdown.Label>
                <Dropdown.Separator />

                {filteredDocumentCategories.map((option) => (
                  <Dropdown.Item
                    key={option.value}
                    onClick={() => setUserDocumentCategoryId(option.value)}
                  >
                    {option.text}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown>
          </>
        )}

        {showDatePicker && (
          <DatePicker
            placeholder="Select year"
            label="Tax Year"
            value={year?.toString() ?? ''}
            maxDate={new Date()}
            onChange={setYear}
            dateFormat={DATE_FORMATS.YEAR}
            showYearPicker
            clearable
            disabled={taxYearDisabled}
            afterLabel={
              <Popup
                content={
                  <Text>
                    A tax year is the 12-month calendar year covered by a tax
                    return. In the U.S., the tax year for individuals runs from
                    Jan. 1 to Dec. 31 and includes taxes owed on earnings during
                    that period. For example, taxes withheld or owed for
                    earnings during the calendar year {currentTaxYear} would be
                    included on the tax return sent to the IRS in {nextTaxYear}.
                    Even though you are sending the tax return in {nextTaxYear},
                    it is for the tax year {currentTaxYear}.
                  </Text>
                }
              />
            }
          />
        )}

        {taxFilingFormId && (
          <Dropdown>
            <Dropdown.Trigger>
              {taxDocumentType
                ? taxDocumentType.charAt(0).toUpperCase() +
                  taxDocumentType.slice(1)
                : 'Select State or Federal'}
            </Dropdown.Trigger>
            <Dropdown.Content>
              <Dropdown.Label>Select State or Federal</Dropdown.Label>
              <Dropdown.Separator />

              <Dropdown.Item onClick={() => setTaxDocumentType('state')}>
                State
              </Dropdown.Item>

              <Dropdown.Item onClick={() => setTaxDocumentType('federal')}>
                Federal
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        )}

        {(sentCategory?.downloadFormUrl || sentCategory?.viewExampleUrl) && (
          <div className="flex gap-4">
            {sentCategory.downloadFormUrl && (
              <div className="w-5/16">
                <Link href={sentCategory.downloadFormUrl} newPage>
                  Download form
                  <Icon icon={regular('square-up-right')} className="ml-1.5" />
                </Link>
              </div>
            )}

            {sentCategory.viewExampleUrl && (
              <div className="w-5/16">
                <Link href={sentCategory.viewExampleUrl} newPage>
                  View Example
                  <Icon icon={regular('square-up-right')} className="ml-1.5" />
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
